import React, {Component, createRef} from 'react'
import propTypes from 'prop-types'
import {get, isNil} from 'lodash'
import iconNames from 'theme/icons'
import Button from 'components/button'
import TreeView from 'components/treeView'
import PartCategoryDetails from 'components/partCategoryDetails'
import {selectFolder} from 'components/treeSearcher/utils'
import {isCategoryActionAllowed} from 'helpers/contactLists'
import {prefix} from 'helpers/component'
import {maxLength} from 'helpers/string'
import {SKILLS} from 'helpers/onboarding'
import {trackEvent} from 'helpers/tracking'
import './priceListTreeView.scss'

class PriceListTreeView extends Component {
  constructor(props) {
    super(props)
    this.getClass = prefix('tree-view-sidebar-')
    this.selectedNodeRef = createRef()
  }

  componentDidMount() {
    const {onLoadPartCategories, onLoadParts} = this.props
    onLoadParts()
    onLoadPartCategories()
    this.scrollToSelectedNode()
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.scrollToSelectedNode && this.props.scrollToSelectedNode) {
      this.scrollToSelectedNode()
    }
  }

  scrollToSelectedNode = () => {
    if (!isNil(get(this.selectedNodeRef, 'current', null))) {
      this.selectedNodeRef.current.scrollIntoView()
    }
  }

  getSidebar = () => {
    const {
      onExpand,
      onCollapse,
      expandedCategories,
      categoryId,
      partCategories,
      headerHeight,
      breadcrumbHeight,
      onShowMovePartCategoryDialog,
      onShowDeletePartCategoryDialog,
      onShowPartCategoryDialog,
      isAddHidden,
      isPartCategoryDetailsExpanded,
      sidebarMaxHeight,
    } = this.props

    const addButton = this.getAddPriceListButton()
    const partCategory = partCategories.find(cat => cat.id === categoryId)

    let sidebarHeight = isPartCategoryDetailsExpanded ?
      `calc(100vh - ${headerHeight + 286 + breadcrumbHeight}px)` :
      `calc(100vh - ${headerHeight + 90 + breadcrumbHeight}px)`

    if (!isNil(sidebarMaxHeight)) {
      sidebarHeight = `${sidebarMaxHeight}px`
    }

    const sidebarStyling = {maxHeight: sidebarHeight, minHeight: sidebarHeight}

    return <div className={this.getClass(['tree-view-add'])} style={sidebarStyling}>
      {!isAddHidden &&
        <div className={this.getClass('add-price-list')}>
          {addButton}
        </div>
      }
      <TreeView
        rootLabel="All Price Lists"
        nodes={this.mapCategoriesToNodes(partCategories)}
        selectedNode={categoryId}
        expandedNodes={expandedCategories}
        onExpand={onExpand}
        onCollapse={onCollapse}
        onSelect={this.onSelectFolder}
        orderNodes={true}
        isActionEnabled={(node) => {
          isCategoryActionAllowed(node.key) && !isNil(node.parentKey)
        }}
        actions={
          this.getActions(
            partCategory,
            onShowDeletePartCategoryDialog,
            onShowMovePartCategoryDialog,
            onShowPartCategoryDialog,
            partCategories)
        }
        selectedNodeRef={this.selectedNodeRef}
      />
    </div>
  }

  getAddPriceListButton = () => {
    const {priceListId} = this.props
    return <Button
      className={this.getClass('add-price-list-button')}
      flat={true}
      icon={iconNames.add}
      tooltip={isNil(priceListId)
        ? 'Add Price List'
        : 'Add Category'}
      size={28}
      onClick={this.onShowAddDialog}
    />
  }

  onShowAddDialog = () => {
    const {priceListId, onShowAddPriceListDialog, onShowPartCategoryDialog} = this.props
    const addAction = isNil(priceListId)
      ? () => onShowAddPriceListDialog()
      : () => onShowPartCategoryDialog()

    addAction()
  }

  getActions = (
    folder,
    onShowDeletePartCategoryDialog,
    onShowMovePartCategoryDialog,
    onShowPartCategoryDialog,
    partCategories
  ) => {
    const {isActionsHidden} = this.props

    return !isActionsHidden
      ? [
        {
          text: 'Add Category',
          icon: iconNames.add,
          onClick: (e, category) => {
            e.stopPropagation()
            const partCategory = partCategories.find(cat => cat.id === category.key)
            onShowPartCategoryDialog(partCategory)
          },
        },
        {
          text: 'Edit',
          icon: iconNames.edit,
          onClick: (e, category) => {
            e.stopPropagation()
            const partCategory = partCategories.find(cat => cat.id === category.key)
            onShowPartCategoryDialog(partCategory, true)
          },
        },
        {
          text: 'Move',
          icon: iconNames.moveStockFilled,
          onClick: (e, category) => {
            e.stopPropagation()
            const partCategory = partCategories.find(cat => cat.id === category.key)
            onShowMovePartCategoryDialog(partCategory)
          },
        },
        {
          key: folder,
          text: 'Delete',
          icon: iconNames.delete,
          onClick: (e, category) => {
            e.stopPropagation()
            const partCategory = partCategories.find(cat => cat.id === category.key)
            onShowDeletePartCategoryDialog(partCategory)
          },
        },
      ]
      : []
  }

  mapCategoriesToNodes = (categories) => {
    const {categoryLabelMaxLength} = this.props

    if (isNil(categories))
      return []

    return categories.map(category => ({
      key: category.id,
      label: maxLength(category.name, categoryLabelMaxLength, true),
      parentKey: category.parentId,
      order: get(category, 'priceList.order', 0),
    }))
  }

  onSelectFolder = (folderId) => {
    const {
      onSelectFolder,
      selectFolderDisabled,
      isRootNodeSelectDisabled,
      isInAddPartSkill,
      onSkillPage,
    } = this.props

    trackEvent('Price List Breadcrumbs', 'Clicked on a folder', 'Folder')

    if (isInAddPartSkill) onSkillPage(SKILLS.ADD_PART.key, 2)

    const {partCategories, onSelectCategory, onSelectPriceList} = this.props

    if (!isRootNodeSelectDisabled && folderId === 0) {
      if (!selectFolderDisabled) {
        onSelectPriceList(null)
      }
      return
    }

    const node = partCategories.find(n => n.id === folderId)
    const selectedFolder = this.mapNodeToFolder(node)

    this.setState({
      categoryDirection: true,
    }, () => {
      if (!isNil(onSelectFolder)) {
        onSelectFolder(selectedFolder)
      }
      if (!selectFolderDisabled) {
        selectFolder(selectedFolder, onSelectCategory, onSelectPriceList, partCategories)
      }
    })
  }

  mapNodeToFolder = (node) => {
    return {category: node, id: node.id, label: node.name}
  }

  getCategoryDetails = () => {
    const {
      categoryId,
      partCategories,
      totalParts,
      taxCodes,
      partTemplates,
      onShowMovePartCategoryDialog,
      onShowDeletePartCategoryDialog,
      onShowPartCategoryDialog,
      isPartCategoryDetailsExpanded,
    } = this.props

    const partCategory = partCategories.find(cat => cat.id === categoryId)

    return <PartCategoryDetails
      partCategory={partCategory}
      totalParts={totalParts}
      taxCodes={taxCodes}
      partTemplates={partTemplates}
      isExpanded={isPartCategoryDetailsExpanded}
      onToggleExpansion={this.onTogglePartCategoryDetailsExpansion}
      onShowMovePartCategoryDialog={onShowMovePartCategoryDialog}
      onShowDeletePartCategoryDialog={onShowDeletePartCategoryDialog}
      onShowPartCategoryDialog={onShowPartCategoryDialog}
    />
  }

  onTogglePartCategoryDetailsExpansion = () => {
    const {onToggleCategoryDetails, isPartCategoryDetailsExpanded} = this.props

    onToggleCategoryDetails(!isPartCategoryDetailsExpanded)
    trackEvent(
      'Part Category Tree Search',
      'Clicked on toggle part category details',
      'Toggle Part Category Details'
    )
  }

  render() {
    const {isCategoryDetailsHidden} = this.props

    const sidebar = this.getSidebar()
    const categoryDetails = this.getCategoryDetails()

    return (
      <div>
        {sidebar}
        {!isCategoryDetailsHidden && categoryDetails}
      </div>
    )
  }
}

PriceListTreeView.propTypes = {
  priceListId: propTypes.number,
  categoryId: propTypes.number,
  expandedCategories: propTypes.array.isRequired,
  partCategories: propTypes.array.isRequired,
  onExpand: propTypes.func.isRequired,
  onCollapse: propTypes.func.isRequired,
  onShowDeletePartCategoryDialog: propTypes.func.isRequired,
  onShowMovePartCategoryDialog: propTypes.func.isRequired,
  onShowPartCategoryDialog: propTypes.func.isRequired,
  onSelectPriceList: propTypes.func.isRequired,
  onSelectCategory: propTypes.func.isRequired,
  onDeSelectAllRows: propTypes.func.isRequired,
  onLoadParts: propTypes.func.isRequired,
  onLoadPartCategories: propTypes.func.isRequired,
  onShowAddPriceListDialog: propTypes.func.isRequired,
  totalParts: propTypes.number,
  taxCodes: propTypes.array.isRequired,
  partTemplates: propTypes.array,
  onSelectFolder: propTypes.func,
  headerHeight: propTypes.number,
  breadcrumbHeight: propTypes.number,
  selectFolderDisabled: propTypes.bool,
  isActionsHidden: propTypes.bool,
  isAddHidden: propTypes.bool,
  isCategoryDetailsHidden: propTypes.bool,
  isRootNodeSelectDisabled: propTypes.bool,
  sidebarMaxHeight: propTypes.number,
  categoryLabelMaxLength: propTypes.number,
  scrollToSelectedNode: propTypes.bool,
  isInAddPartSkill: propTypes.bool.isRequired,
  onSkillPage: propTypes.func.isRequired,
  onToggleCategoryDetails: propTypes.func.isRequired,
  isPartCategoryDetailsExpanded: propTypes.bool.isRequired,
}

PriceListTreeView.defaultProps = {
  expandedCategories: [],
  partCategories: [],
  totalParts: 0,
  taxCodes: [],
  partTemplates: [],
  headerHeight: 0,
  breadcrumbHeight: 0,
  selectFolderDisabled: false,
  isActionsHidden: false,
  isAddHidden: false,
  isCategoryDetailsHidden: false,
  categoryLabelMaxLength: 22,
  isRootNodeSelectDisabled: false,
}

export default PriceListTreeView
